import { useCallback, useEffect, useRef, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import type { BlockGrid, BlockSlot } from '@/core/time/blockGrid'
import { entryAt, erase, upsert, type EntryContent } from '@/core/repository/entryRepository'
import { listObjects } from '@/core/repository/objectRepository'
import { db } from '@/db/schema'
import { labelColorOn } from '@/design/contrast'
import { logger } from '@/core/logging/logger'
import { ObjectPicker } from './ObjectPicker'
import './detail.css'

interface BlockDetailProps {
  grid: BlockGrid
  slot: BlockSlot
  /** Opens with the comment field focused, for long-press-on-chip. */
  focusComment?: boolean
  onClose: () => void
  onChanged: () => void
}

/**
 * Edits one block: type, object, comment, planned flag.
 *
 * The comment is the diary. It is the reason this is a sheet rather than a
 * popover -- a block worth annotating deserves room to type.
 */
export function BlockDetail({
  grid,
  slot,
  focusComment = false,
  onClose,
  onChanged,
}: BlockDetailProps) {
  const commentRef = useRef<HTMLTextAreaElement>(null)
  const [showObjects, setShowObjects] = useState(false)

  const entry = useLiveQuery(() => entryAt(slot), [slot.dayKey, slot.slotIndex])
  const eventTypes = useLiveQuery(
    () => db.eventTypes.filter((t) => t.deletedAt === null && t.isArchived === 0).sortBy('sortOrder'),
    [],
    [],
  )
  const objects = useLiveQuery(() => listObjects(false), [], [])

  /**
   * Local edits, if any. Null means "show what is in the database".
   *
   * Derived rather than synced with an effect: copying the entry into state
   * whenever the live query fires would both cascade a render and risk
   * overwriting what the user is halfway through typing.
   */
  const [draft, setDraft] = useState<EntryContent | null>(null)

  const current: EntryContent = draft ?? {
    eventTypeId: entry?.eventTypeId ?? null,
    eventObjectId: entry?.eventObjectId ?? null,
    comment: entry?.comment ?? null,
    isPlanned: entry?.isPlanned === 1,
  }

  useEffect(() => {
    if (focusComment) {
      const t = setTimeout(() => commentRef.current?.focus(), 60)
      return () => clearTimeout(t)
    }
    return undefined
  }, [focusComment])

  const save = useCallback(
    (next: EntryContent) => {
      setDraft(next)
      void upsert(grid, next, [slot], {})
        .then(onChanged)
        .catch((err: unknown) => logger.error('db', 'Could not save block', err))
    },
    [grid, slot, onChanged],
  )

  const handleDelete = useCallback(() => {
    void erase([slot])
      .then(() => {
        onChanged()
        onClose()
      })
      .catch((err: unknown) => logger.error('db', 'Could not delete block', err))
  }, [slot, onChanged, onClose])

  const startMs = grid.startOfSlot(slot.dayKey, slot.slotIndex)
  const endMs = startMs + grid.slotDurationMs
  const timeFormat = new Intl.DateTimeFormat(undefined, {
    timeZone: grid.timeZone,
    hour: '2-digit',
    minute: '2-digit',
  })
  const dateFormat = new Intl.DateTimeFormat(undefined, {
    timeZone: grid.timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })

  const objectName = objects.find((o) => o.id === current.eventObjectId)?.name ?? null

  return (
    <div className="bd-sheet" role="dialog" aria-label="Edit block">
      <header className="bd-head">
        <button className="bd-cancel" onClick={onClose}>
          Done
        </button>
        <span className="bd-when">
          {dateFormat.format(new Date(startMs))} &middot; {timeFormat.format(new Date(startMs))}
          {' – '}
          {timeFormat.format(new Date(endMs))}
        </span>
      </header>

      <div className="bd-body scroll-y">
        <section className="bd-section">
          <h3 className="bd-label">Event</h3>
          <div className="bd-chips">
            {eventTypes.map((type) => (
              <button
                key={type.id}
                className={`bd-chip${current.eventTypeId === type.id ? ' bd-chip--on' : ''}`}
                style={{ background: type.colorHex, color: labelColorOn(type.colorHex) }}
                onClick={() => save({ ...current, eventTypeId: type.id })}
              >
                {type.shortcutText || type.name}
              </button>
            ))}
          </div>
        </section>

        <section className="bd-section">
          <h3 className="bd-label">Object</h3>
          <button className="bd-row" onClick={() => setShowObjects(true)}>
            <span>{objectName ?? 'None'}</span>
            <span className="bd-chevron" aria-hidden="true">
              ›
            </span>
          </button>
        </section>

        <section className="bd-section">
          <h3 className="bd-label">Comment</h3>
          <textarea
            ref={commentRef}
            className="bd-comment"
            value={current.comment ?? ''}
            placeholder="What happened?"
            rows={4}
            onChange={(e) => setDraft({ ...current, comment: e.target.value || null })}
            // Saved on blur rather than per keystroke: a write on every letter
            // would churn the sync queue and the run cache for no benefit.
            onBlur={() => save(current)}
          />
        </section>

        <section className="bd-section">
          <label className="bd-row bd-row--toggle">
            <span>Planned</span>
            <input
              type="checkbox"
              checked={current.isPlanned}
              onChange={(e) => save({ ...current, isPlanned: e.target.checked })}
            />
          </label>
          <p className="bd-hint">Marks this as an intention rather than a record.</p>
        </section>

        <section className="bd-section">
          <button className="bd-delete" onClick={handleDelete}>
            Clear this block
          </button>
        </section>
      </div>

      {showObjects ? (
        <ObjectPicker
          value={current.eventObjectId}
          onChange={(eventObjectId) => save({ ...current, eventObjectId })}
          onClose={() => setShowObjects(false)}
        />
      ) : null}
    </div>
  )
}
